import React, { useState, useEffect, useRef } from 'react';
import {
  generateAndDownloadNDA,
  generateAndDownloadSQA,
  generateAndDownload4MDeclaration,
  generateAndDownloadCodeOfConduct,
  generateAndDownloadComplianceAgreement,
  generateAndDownloadSelfDeclaration,
  generateNDABlob,
  generateSQABlob,
  generate4MDeclarationBlob,
  generateCodeOfConductBlob,
  generateComplianceAgreementBlob,
  generateSelfDeclarationBlob,
} from '../../../utils/documentGenerator.jsx';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { getCountryName } from '../../../utils/countries';
import {
  formatSupplierCategory,
  formatSupplierType,
  formatBusinessVertical,
  formatAccountType,
  formatSupplierGroup
} from '../../../utils/formatters';
import IndianVendorQuestionnaire from './IndianVendorQuestionnaire';
import ForeignVendorQuestionnaire from './ForeignVendorQuestionnaire';
import { API_BASE_URL } from '../../../config/api';
import { documentService, approvalService, acknowledgementService, vendorService, getErrorMessage } from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const ReviewPanel = ({
  application,
  isAdmin = false,
  onClose,
  onApprove,
  onReject,
  onRequestChanges,
  onAssignVendorCode,
  onQuestionnaireSent,
  onRefreshApplication,
}) => {
  const toast = useToast();
  const { isAdmin: authIsAdmin, isCoordinator } = useAuth();
  const canRegenerateMsme = authIsAdmin || isCoordinator;
  const [activeTab, setActiveTab] = useState('supplier');
  const [remarks, setRemarks] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showRejectionOptions, setShowRejectionOptions] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showPermanentRejectionConfirmation, setShowPermanentRejectionConfirmation] = useState(false);
  const [rejectionType, setRejectionType] = useState(null); // 'permanent' | 'rectification'
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showAssignCodeModal, setShowAssignCodeModal] = useState(false);
  const [assignCodeValue, setAssignCodeValue] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState(new Set());
  const [showMultiRejectModal, setShowMultiRejectModal] = useState(false);
  const [multiRejectReason, setMultiRejectReason] = useState('');
  const [regeneratingMsme, setRegeneratingMsme] = useState(false);
  const isRejectingRef = useRef(false);
  const isApprovingRef = useRef(false);

  // Questionnaire form data
  const [questionnaireData, setQuestionnaireData] = useState({
    supplierTermOfPayment: '',
    supplierPaymentMethod: '',
    supplierDeliveryTerms: '',
    supplierModeOfDelivery: '',
    supplierGroup: '',
    supplierCategory: '',
    commodityCode: '',
    tdsApplicable: '',
    tdsSection: '',
    tdsRate: ''
  });
  const [questionnaireErrors, setQuestionnaireErrors] = useState({});
  /** Which agreement id ('nda', 'sqa', …) is currently generating for preview; only that row shows loading. */
  const [previewingAgreementId, setPreviewingAgreementId] = useState(null);

  // Pre-populate questionnaire for LEVEL_2 approvals
  useEffect(() => {
    if (application?.approval_level === 'LEVEL_2' && application) {
      // Check if vendor has questionnaire data populated
      const hasQuestionnaireData = 
        application.supplier_term_of_payment ||
        application.supplier_payment_method ||
        application.supplier_delivery_terms ||
        application.supplier_mode_of_delivery;

      if (hasQuestionnaireData) {
        setQuestionnaireData({
          supplierTermOfPayment: application.supplier_term_of_payment || '',
          supplierPaymentMethod: application.supplier_payment_method || '',
          supplierDeliveryTerms: application.supplier_delivery_terms || '',
          supplierModeOfDelivery: application.supplier_mode_of_delivery || '',
          supplierGroup: application.supplier_group || '',
          supplierCategory: application.supplier_category || '',
          commodityCode: application.commodity_code || ''
        });
        console.log('✅ Pre-populated questionnaire from resolved vendor data');
      }
    }
  }, [application]);

  // Human-friendly document type labels
  const formatDocumentType = (docType) => {
    if (!docType) return 'Document';
    const map = {
      gst_certificate: 'GST Certificate',
      pan_card: 'PAN Card',
      bank_statement: 'Bank Statement',
      msme_certificate: 'MSME Certificate',
      msme_declaration: 'MSME Declaration',
      company_registration: 'Company Registration',
      incorporation_certificate: 'Incorporation Certificate',
      business_license: 'Business License',
      insurance_certificate: 'Insurance Certificate',
      quality_certificate: 'Quality Certificate',
      tax_certificate: 'Tax Certificate',
      other: 'Other Document'
    };
    if (map[docType]) return map[docType];
    // Fallback: titleize with spaces
    return String(docType)
      .split('_')
      .map(w => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
  };

  const tabs = [
    { id: 'supplier', label: 'Supplier Info', icon: 'Building2' },
    { id: 'bank', label: 'Bank Details', icon: 'CreditCard' },
    { id: 'documents', label: 'Documents', icon: 'FileText' },
    { id: 'agreements', label: 'Agreements', icon: 'FileCheck' },
    { id: 'history', label: 'History', icon: 'Clock' }
  ];

  const isDocumentVerificationMode = application?.approval_level === 'LEVEL_1' && 
    application?.documents && 
    application.documents.some(doc => doc.status === 'PENDING');

  const documentsEligibleForReplacementRequest = (application?.documents || []).filter(
    (doc) =>
      doc.status === 'PENDING' &&
      String(doc.document_type || '').toLowerCase() !== 'msme_declaration'
  );

  const hasMsmeCertificate = (application?.documents || []).some(
    (doc) => String(doc.document_type || '').toLowerCase() === 'msme_certificate'
  );
  const documentsForReviewDisplay = (application?.documents || []).filter(
    (doc) =>
      !(hasMsmeCertificate && String(doc.document_type || '').toLowerCase() === 'msme_declaration')
  );

  // Default to Documents tab when entering Document Verification mode only (not on every tab click).
  // Previously activeTab was in the dependency array, which reset the tab to Documents whenever
  // the user selected Supplier / Bank / etc., so sub-tabs appeared broken.
  useEffect(() => {
    if (isDocumentVerificationMode) {
      setActiveTab('documents');
    }
  }, [isDocumentVerificationMode]);

  const rejectionReasons = [
    { value: 'incomplete_documents', label: 'Incomplete Documents' },
    { value: 'invalid_information', label: 'Invalid Information' },
    { value: 'compliance_issues', label: 'Compliance Issues' },
    { value: 'duplicate_vendor', label: 'Duplicate Vendor' },
    { value: 'other', label: 'Other (specify)' }
  ];

  // Check if vendor is Indian
  const isIndianVendor = application.country_origin === 'IN';

  // Check if we're in Document Verification mode
  // Document Verification mode: LEVEL_1 approval with at least one PENDING document
  

  // Check if all documents are verified
  const allDocumentsVerified = application?.documents && 
    application.documents.length > 0 && 
    application.documents.every(doc => doc.status === 'VERIFIED');

  const updateQuestionnaireData = (updates) => {
    setQuestionnaireData(prev => ({ ...prev, ...updates }));
    // Clear errors for updated fields
    const updatedFields = Object.keys(updates);
    setQuestionnaireErrors(prev => {
      const newErrors = { ...prev };
      updatedFields.forEach(field => delete newErrors[field]);
      return newErrors;
    });
  };

  const validateQuestionnaire = () => {
    const errors = {};
    if (!questionnaireData.supplierTermOfPayment) errors.supplierTermOfPayment = 'Required';
    if (!questionnaireData.supplierPaymentMethod) errors.supplierPaymentMethod = 'Required';
    if (!questionnaireData.supplierDeliveryTerms) errors.supplierDeliveryTerms = 'Required';
    if (!questionnaireData.supplierModeOfDelivery) errors.supplierModeOfDelivery = 'Required';
    if (!questionnaireData.supplierGroup) errors.supplierGroup = 'Required';
    if (!questionnaireData.supplierCategory) errors.supplierCategory = 'Required';
    if (!questionnaireData.commodityCode) errors.commodityCode = 'Required';
    if (questionnaireData.tdsApplicable === 'YES') {
      if (!questionnaireData.tdsSection) errors.tdsSection = 'Required when TDS is applicable';
      if (!questionnaireData.tdsRate) {
        errors.tdsRate = 'Required when TDS is applicable';
      } else if (!/^\d+(\.\d+)?$/.test(questionnaireData.tdsRate.trim()) || parseFloat(questionnaireData.tdsRate) > 100) {
        errors.tdsRate = 'Enter a percentage between 0 and 100';
      }
    }
    return errors;
  };

  console.log(application);

  // Helper function to fetch digital signature and convert to base64 data URL
  const fetchDigitalSignature = async (vendorId) => {
    try {
      // Fetch all documents for the vendor (uses apiClient with cookie credentials)
      const response = await documentService.getVendorDocuments(vendorId);
      const documents = response?.data ?? [];

      // Find the digital signature document
      const digitalSignatureDoc = documents.find(doc => doc.document_type === 'DIGITAL_SIGNATURE');

      if (!digitalSignatureDoc) {
        console.warn('Digital signature document not found for vendor');
        return null;
      }

      // Download the digital signature file using authenticated endpoint
      const downloadResponse = await documentService.downloadDocument(digitalSignatureDoc.id);
      if (!downloadResponse || !downloadResponse.data) {
        console.error('Failed to download digital signature');
        return null;
      }

      // Convert to blob then to base64 data URL
      const blob = new Blob([downloadResponse.data], { type: downloadResponse.headers['content-type'] || 'application/octet-stream' });
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching digital signature:', error);
      return null;
    }
  };

  const getVendorData = async () => {
    // Fetch digital signature
    const digitalSignature = await fetchDigitalSignature(application.id);

    return {
      // Core identity
      companyName: application.company_name,
      contactPersonName: application.contact_person_name,
      designation: application.designation,
      // Submission date for document rendering
      submissionDate: application.created_at || application.createdAt,
      // Addresses
      registeredAddress: application.registered_address,
      registeredCity: application.registered_city,
      registeredState: application.registered_state,
      registeredCountry: application.registered_country,
      registeredPincode: application.registered_pincode,
      supplyAddress: application.supply_address,
      supplyCity: application.supply_city,
      supplyState: application.supply_state,
      supplyPincode: application.supply_pincode,
      supplyCountry: application.supply_country,
      // Origin and business
      countryOrigin: application.country_origin,
      businessVertical: application.business_vertical,
      businessDescription: application.business_description,
      // Include snake_case duplicate for any generator that still reads it
      business_vertical: application.business_vertical,
      // Digital signature as base64 data URL
      digitalSignature: digitalSignature,
    };
  };

  const handleDownloadAgreement = async (agreementType) => {
    const vendorData = await getVendorData();

    switch (agreementType) {
      case 'nda':
        return generateAndDownloadNDA(vendorData);
      case 'sqa':
        return generateAndDownloadSQA(vendorData);
      case 'four_m':
        return generateAndDownload4MDeclaration(vendorData);
      case 'code_of_conduct':
        return generateAndDownloadCodeOfConduct(vendorData);
      case 'compliance_agreement':
        return generateAndDownloadComplianceAgreement(vendorData);
      case 'self_declaration':
        return generateAndDownloadSelfDeclaration(vendorData);
      default:
        console.warn('Unknown agreement type:', agreementType);
    }
  };

  const handlePreviewAgreement = async (agreementType) => {
    try {
      setPreviewingAgreementId(agreementType);
      const vendorData = await getVendorData();

      console.log('Generating agreement for preview:', agreementType);
      console.log('Vendor data:', vendorData);

      let pdfBlob;

      switch (agreementType) {
        case 'nda':
          pdfBlob = await generateNDABlob(vendorData);
          break;
        case 'sqa':
          pdfBlob = await generateSQABlob(vendorData);
          break;
        case 'four_m':
          pdfBlob = await generate4MDeclarationBlob(vendorData);
          break;
        case 'code_of_conduct':
          pdfBlob = await generateCodeOfConductBlob(vendorData);
          break;
        case 'compliance_agreement':
          pdfBlob = await generateComplianceAgreementBlob(vendorData);
          break;
        case 'self_declaration':
          pdfBlob = await generateSelfDeclarationBlob(vendorData);
          break;
        default:
          console.warn('Unknown agreement type:', agreementType);
          return;
      }

      if (pdfBlob) {
        // Create object URL and open in new tab
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
        // Clean up the object URL after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (err) {
      console.error('Failed to generate agreement PDF:', err);
      toast.error('Failed to generate agreement PDF. Please try again.');
    } finally {
      setPreviewingAgreementId(null);
    }
  };

  // generateAgreementContent removed; using centralized generators in documentGenerator

  const handleAction = async (action) => {
    if (action === 'reject') {
      setShowRejectionOptions(true);
      return;
    }

    if (action === 'approve') {
      if (isProcessing || isApprovingRef.current) return;
      isApprovingRef.current = true;
      // Document verification mode: verify docs ONLY - do NOT trigger Level 1 approval
      // Vendor will move to Level 1 tab where admin must explicitly Approve or Reject
      if (isDocumentVerificationMode) {
        setIsProcessing(true);
        try {
          const pendingDocs = application?.documents?.filter(doc => doc.status === 'PENDING') || [];
          if (pendingDocs.length > 0) {
            for (const doc of pendingDocs) {
              await documentService.updateStatus(doc.id, 'VERIFIED');
            }
            toast.success('Documents verified. Vendor now appears under the Documents Verified tab for the next approval step.');
            if (onQuestionnaireSent) onQuestionnaireSent();
          } else if (allDocumentsVerified) {
            toast.success('All documents already verified. Vendor is listed under the Documents Verified tab.');
          }
        } catch (error) {
          console.error('Document verification failed:', error);
          toast.error('Failed to verify documents. Please try again.');
        } finally {
          isApprovingRef.current = false;
          setIsProcessing(false);
        }
        return;
      }
      // For LEVEL_1 (non-doc-verification): Approve then open questionnaire modal - do NOT close panel
      if (application?.approval_level === 'LEVEL_1') {
        setIsProcessing(true);
        try {
          await onApprove(application.id, 'First approval', {});
          toast.success('Approval recorded. Please fill the questionnaire below.');
          setShowQuestionnaire(true);
          if (onQuestionnaireSent) onQuestionnaireSent();
          // Do NOT call onClose() - keep panel open until questionnaire is submitted
        } catch (error) {
          console.error('Approval failed:', error);
        } finally {
          isApprovingRef.current = false;
          setIsProcessing(false);
        }
        return;
      }

      // LEVEL_2: No approve action - use Assign Vendor Code button instead
      
      // For other levels or fallback
      setIsProcessing(true);
      try {
        await onApprove(application.id, 'Approval', {});
        onClose();
      } catch (error) {
        console.error('Approval failed:', error);
      } finally {
        isApprovingRef.current = false;
        setIsProcessing(false);
      }
      return;
    }

    // For request_changes only - guard against fallthrough
    if (action !== 'request_changes') return;

    setIsProcessing(true);
    try {
      await onRequestChanges(application.id, {});
      onClose();
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const isSubmittingQuestionnaireRef = useRef(false);

  const handleQuestionnaireSubmit = async () => {
    if (isSubmittingQuestionnaireRef.current) return;

    isSubmittingQuestionnaireRef.current = true;
    // ... rest of the logic
    if (Object.keys(questionnaireErrors).length > 0) {
      setQuestionnaireErrors(questionnaireErrors);
      toast.warning('Please complete all questionnaire fields');
      return;
    }

    // Check if admin
    if (!isAdmin) {
      toast.error('Only admins can fill the questionnaire');
      return;
    }

    setIsProcessing(true);

    try {
      // Call backend endpoint to fill questionnaire (sends acknowledgement email to vendor)
      await approvalService.fillQuestionnaire(application.id, {
        supplier_term_of_payment: questionnaireData.supplierTermOfPayment,
        supplier_payment_method: questionnaireData.supplierPaymentMethod,
        supplier_delivery_terms: questionnaireData.supplierDeliveryTerms,
        supplier_mode_of_delivery: questionnaireData.supplierModeOfDelivery,
        supplier_group: questionnaireData.supplierGroup,
        supplier_category: questionnaireData.supplierCategory,
        commodity_code: questionnaireData.commodityCode,
        tds_applicable: questionnaireData.tdsApplicable || undefined,
        tds_section: questionnaireData.tdsApplicable === 'YES' ? questionnaireData.tdsSection : undefined,
        tds_rate: questionnaireData.tdsApplicable === 'YES' ? questionnaireData.tdsRate.trim() : undefined,
      });

      toast.success('Questionnaire sent to vendor successfully');
      setShowQuestionnaire(false);
      if (onQuestionnaireSent) onQuestionnaireSent();
      onClose();
    } catch (error) {
      console.error('Questionnaire submission failed:', error);
      toast.error('Failed to save questionnaire. Please try again.');
      } finally {
        setIsProcessing(false);
        isSubmittingQuestionnaireRef.current = false;
      }
  };

  const handleAssignVendorCodeSubmit = async () => {
    if (!onAssignVendorCode) return;
    setIsProcessing(true);
    try {
      await onAssignVendorCode(application.id, assignCodeValue || undefined);
      setShowAssignCodeModal(false);
      setAssignCodeValue('');
      onClose();
    } catch (error) {
      console.error('Assign vendor code failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectRejectionType = (type) => {
    setRejectionType(type);
    setShowRejectionOptions(false);
    
    if (type === 'permanent') {
      setShowPermanentRejectionConfirmation(true);
    } else if (type === 'rectification') {
      setShowRejectionForm(true);
    }
  };

  const handleConfirmPermanentRejection = async () => {
    setShowPermanentRejectionConfirmation(false);
    setShowRejectionForm(true);
  };

  const handleCancelRejection = () => {
    setShowRejectionOptions(false);
    setShowPermanentRejectionConfirmation(false);
    setShowRejectionForm(false);
    setRejectionType(null);
    setRejectionReason('');
    setCustomReason('');
    setRemarks('');
  };

  const handleReject = async () => {
    // For rectification, remarks are mandatory
    if (rejectionType === 'rectification' && !remarks.trim()) {
      toast.warning('Please provide additional remarks for rectification');
      return;
    }

    // For permanent rejection, reason is required
    if (rejectionType === 'permanent') {
      if (!rejectionReason) {
        toast.warning('Please select a rejection reason');
        return;
      }

      if (rejectionReason === 'other' && !customReason.trim()) {
        toast.warning('Please specify the custom reason');
        return;
      }
    }

    setIsProcessing(true);

    try {
      let finalReason = '';
      if (rejectionType === 'permanent') {
        // Get the proper label for the rejection reason
        if (rejectionReason === 'other') {
          finalReason = customReason;
        } else {
          const reasonOption = rejectionReasons.find(r => r.value === rejectionReason);
          finalReason = reasonOption ? reasonOption.label : rejectionReason;
        }
      }

      // Combine reason with additional remarks
      const fullRejectionMessage = rejectionType === 'permanent' 
        ? (remarks ? `${finalReason} - ${remarks}` : finalReason)
        : remarks;
      
      await onReject(application.id, fullRejectionMessage, rejectionReason === 'other' ? customReason : rejectionReason, remarks, rejectionType);
      onClose();
    } catch (error) {
      console.error('Rejection failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderSupplierInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Company Name</label>
          <p className="text-slate-900 font-medium">{application.company_name}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Business Vertical</label>
          <p className="text-slate-900">{formatBusinessVertical(application.business_vertical) || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Category</label>
          <p className="text-slate-900">{formatSupplierCategory(application.supplier_category) || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Supplier Group</label>
          <p className="text-slate-900">{formatSupplierType(formatSupplierGroup(application.supplier_group)) || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Person</label>
          <p className="text-slate-900 font-medium">{application.contact_person_name || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
          <p className="text-slate-900">{application.email || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Phone</label>
          <p className="text-slate-900">{application.phone_number || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Country</label>
          <p className="text-slate-900">{getCountryName(application.country_origin) || 'N/A'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Registered Address</label>
          <div className="text-slate-900 whitespace-normal break-words">
            {application.registered_address && (
              <p className="mb-1">{application.registered_address}</p>
            )}
            <p className="text-sm">
              {[application.registered_city, application.registered_state]
                .filter(Boolean)
                .join(', ')}
              {application.registered_pincode && ` - ${application.registered_pincode}`}
              {!application.registered_city && !application.registered_state && !application.registered_pincode && 'N/A'}
            </p>
            {application.registered_country && (
              <p className="text-sm mt-1">{getCountryName(application.registered_country)}</p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Supply Address</label>
          <div className="text-slate-900 whitespace-normal break-words">
            {application.supply_address && (
              <p className="mb-1">{application.supply_address}</p>
            )}
            <p className="text-sm">
              {[application.supply_city, application.supply_state]
                .filter(Boolean)
                .join(', ')}
              {application.supply_pincode && ` - ${application.supply_pincode}`}
              {!application.supply_city && !application.supply_state && !application.supply_pincode && 'N/A'}
            </p>
            {application.supply_country && (
              <p className="text-sm mt-1">{getCountryName(application.supply_country)}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderBankDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Bank Name</label>
          <p className="text-slate-900 font-medium">{application.bank_name || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Account Number</label>
          <p className="text-slate-900">{application.account_number || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Account Type</label>
          <p className="text-slate-900">{formatAccountType(application.account_type) || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">IFSC/Swift Code</label>
          <p className="text-slate-900 font-mono">
            {application.ifsc_code || application.swift_code || (application.bank_info && application.bank_info.swift_code) || 'N/A'}
          </p>
        </div>
        {application.country_origin !== 'IN' && (application.swift_code || application.bank_info?.swift_code) && (
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Swift Code (Foreign)</label>
            <p className="text-slate-900 font-mono">{application.swift_code || application.bank_info?.swift_code}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Branch</label>
          <p className="text-slate-900">{application.branch_name || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Currency</label>
          <p className="text-slate-900">{application.currency || 'N/A'}</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Bank Proof</label>
          <div className="flex items-center gap-2">
            <Icon name="FileText" size={16} className="text-blue-600" />
            <span className="text-slate-900">Bank proof document uploaded</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Handle document replacement request (reject selected docs, send replacement link to vendor)
  const handleRejectSelectedDocuments = async () => {
    if (isProcessing || isRejectingRef.current) return;
    if (selectedDocuments.size === 0) {
      toast.warning('Please select at least one document to reject');
      return;
    }

    if (!multiRejectReason.trim()) {
      toast.warning('Please provide a rejection reason');
      return;
    }

    isRejectingRef.current = true;
    setIsProcessing(true);
    try {
      const documentIds = Array.from(selectedDocuments)
        .map((id) => parseInt(id, 10))
        .filter((id) => {
          const doc = application?.documents?.find((d) => d.id === id);
          return doc && String(doc.document_type || '').toLowerCase() !== 'msme_declaration';
        });
      if (documentIds.length === 0) {
        toast.warning('Please select at least one document to reject');
        return;
      }
      await documentService.rejectMultipleDocuments(application.id, documentIds, multiRejectReason.trim());
      
      toast.success(`${documentIds.length} document(s) - vendor will receive an email with replacement link.`);
      
      // Clear selection and close modal
      setSelectedDocuments(new Set());
      setShowMultiRejectModal(false);
      setMultiRejectReason('');
      
      // Refresh application data
      if (onQuestionnaireSent) {
        onQuestionnaireSent();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error rejecting documents:', error);
      toast.error('Failed to reject documents. Please try again.');
    } finally {
      isRejectingRef.current = false;
      setIsProcessing(false);
    }
  };

  // Toggle document selection
  const toggleDocumentSelection = (documentId) => {
    const newSelection = new Set(selectedDocuments);
    if (newSelection.has(documentId)) {
      newSelection.delete(documentId);
    } else {
      newSelection.add(documentId);
    }
    setSelectedDocuments(newSelection);
  };

  const handleRegenerateMsmeDeclaration = async () => {
    try {
      setRegeneratingMsme(true);
      await vendorService.regenerateMsmeDeclaration(application.id);
      toast.success('MSME declaration regeneration started.');
      if (onRefreshApplication) {
        await onRefreshApplication();
      }
    } catch (err) {
      console.error('Error regenerating MSME declaration:', err);
      toast.error(getErrorMessage(err) || 'Failed to regenerate MSME declaration.');
    } finally {
      setRegeneratingMsme(false);
    }
  };

  const renderDocuments = () => {
    // Show message if all documents are verified
    if (isDocumentVerificationMode && allDocumentsVerified) {
      return (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <Icon name="CheckCircle" size={48} className="text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">All Documents Verified</h3>
            <p className="text-green-700">
              Document verification completed. The vendor can continue in the Documents Verified tab for the next approval step.
            </p>
          </div>
          {documentsForReviewDisplay.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documentsForReviewDisplay.map((doc, index) => (
                <div key={index} className="border border-border rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Icon name="CheckCircle" size={20} className="text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-card-foreground">{formatDocumentType(doc.document_type)}</h5>
                      <p className="text-xs text-text-secondary">{doc.file_name}</p>
                      <span className="text-xs text-green-600 font-medium">Verified</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-text-secondary">
                      {doc.expiry_date ? `Expires: ${new Date(doc.expiry_date).toLocaleDateString()}` : 'No expiry'}
                    </span>
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await documentService.downloadDocument(doc.id);
                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch (e) {
                            console.error(e);
                            toast.error('Preview failed. Please try again.');
                          }
                        }}
                      >
                        <Icon name="Eye" size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await documentService.downloadDocument(doc.id);
                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = doc.file_name || 'document';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (e) {
                            console.error(e);
                            toast.error('Download failed. Please try again.');
                          }
                        }}
                      >
                        <Icon name="Download" size={14} />
                      </Button>
                      {canRegenerateMsme &&
                        String(doc.document_type || '').toLowerCase() === 'msme_declaration' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRegenerateMsmeDeclaration}
                            disabled={regeneratingMsme}
                            iconName="RefreshCw"
                            iconPosition="left"
                          >
                            {regeneratingMsme ? 'Regenerating...' : 'Regenerate'}
                          </Button>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {isDocumentVerificationMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              <Icon name="Info" size={16} className="inline mr-2" />
              Review documents. Click Approve to accept all, or Request Document Replacement to ask vendor to replace specific documents.
            </p>
          </div>
        )}

        {documentsForReviewDisplay.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documentsForReviewDisplay.map((doc, index) => {
              const isPending = doc.status === 'PENDING';
              const isVerified = doc.status === 'VERIFIED';
              const isRejected = doc.status === 'REJECTED';
              
              return (
                <div key={index} className={`border rounded-lg p-4 ${
                  isVerified ? 'border-green-200 bg-green-50' : 
                  isRejected ? 'border-red-200 bg-red-50' : 
                  'border-border'
                }`}>
                  {doc.replaced_at && (
                    <span className="inline-block text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded mb-2">Resubmitted</span>
                  )}
                  <div className="flex items-start space-x-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isVerified ? 'bg-green-100' : 
                      isRejected ? 'bg-red-100' : 
                      'bg-primary/10'
                    }`}>
                      <Icon 
                        name={isVerified ? 'CheckCircle' : isRejected ? 'XCircle' : 'FileText'} 
                        size={20} 
                        className={isVerified ? 'text-green-600' : isRejected ? 'text-red-600' : 'text-primary'} 
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h5 className="font-medium text-card-foreground">{formatDocumentType(doc.document_type)}</h5>
                      <p className="text-xs text-text-secondary truncate">{doc.file_name}</p>
                      {isVerified && <span className="text-xs text-green-600 font-medium">Verified</span>}
                      {isRejected && <span className="text-xs text-red-600 font-medium">Rejected</span>}
                      {isPending && <span className="text-xs text-yellow-600 font-medium">Pending</span>}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-text-secondary">
                      {doc.expiry_date ? `Expires: ${new Date(doc.expiry_date).toLocaleDateString()}` : 'No expiry'}
                    </span>
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await documentService.downloadDocument(doc.id);
                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch (e) {
                            console.error(e);
                            toast.error('Preview failed. Please try again.');
                          }
                        }}
                      >
                        <Icon name="Eye" size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await documentService.downloadDocument(doc.id);
                            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = doc.file_name || 'document';
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (e) {
                            console.error(e);
                            toast.error('Download failed. Please try again.');
                          }
                        }}
                      >
                        <Icon name="Download" size={14} />
                      </Button>
                      {canRegenerateMsme &&
                        String(doc.document_type || '').toLowerCase() === 'msme_declaration' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRegenerateMsmeDeclaration}
                            disabled={regeneratingMsme}
                            iconName="RefreshCw"
                            iconPosition="left"
                          >
                            {regeneratingMsme ? 'Regenerating...' : 'Regenerate'}
                          </Button>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="FileText" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No documents uploaded yet</p>
          </div>
        )}
      </div>
    );
  };

  const renderAgreements = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        {(() => {
          const items = [
            { id: 'nda', label: 'Non-Disclosure Agreement (NDA)', signed: application.nda },
            { id: 'sqa', label: 'Supplier Agreement', signed: application.sqa },
            { id: 'four_m', label: '4M Agreement', signed: application.four_m },
            { id: 'code_of_conduct', label: 'Code of Conduct', signed: application.code_of_conduct },
            { id: 'compliance_agreement', label: 'Compliance Agreement', signed: application.compliance_agreement },
            { id: 'self_declaration', label: 'Self Declaration', signed: application.self_declaration },
            { id: 'overseas_agreement', label: 'Overseas Agreement', signed: application.overseas_agreement }
          ].filter(a => !!a.signed);

          if (items.length === 0) {
            return (
              <div className="text-center py-8">
                <Icon name="FileX" size={48} className="text-text-secondary mx-auto mb-4" />
                <p className="text-text-secondary">No signed agreements</p>
                <p className="text-xs text-text-secondary mt-2">Documents will be generated automatically during approval</p>
              </div>
            );
          }

          return items.map((ag) => (
            <div key={ag.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <Checkbox checked disabled className="pointer-events-none" />
                <div>
                  <h5 className="font-medium text-card-foreground">{ag.label}</h5>
                  <p className="text-sm text-text-secondary">Signed</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">Signed</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreviewAgreement(ag.id)}
                  disabled={previewingAgreementId !== null}
                >
                  {previewingAgreementId === ag.id ? (
                    <Icon name="Loader" size={14} className="animate-spin" />
                  ) : (
                    <Icon name="Eye" size={14} />
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDownloadAgreement(ag.id)}>
                  <Icon name="Download" size={14} />
                </Button>
              </div>
            </div>
          ));
        })()}
      </div>

      {/* Information about automatic document generation */}
      <div className="mt-6 p-4 bg-info/10 border border-info/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={16} className="text-info mt-0.5" />
          <div>
            <h4 className="font-medium text-info">Automatic Document Generation</h4>
            <p className="text-sm text-text-secondary mt-1">
              When you approve this vendor, all required PDF documents (NDA, SQA, 4M Declaration, etc.)
              will be automatically generated and attached to the approval email along with the Excel file.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderQuestionnaire = () => {
    // Check if there's questionnaire data in the application history
    const questionnaireEntry = application.history?.find(event =>
      event.comments && event.comments.includes('Questionnaire completed:')
    );

    if (!questionnaireEntry) {
      return (
        <div className="text-center py-8">
          <Icon name="ClipboardList" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No questionnaire data available</p>
          <p className="text-xs text-text-secondary mt-2">Questionnaire will be filled during approval process</p>
        </div>
      );
    }

    // Extract questionnaire data from comments
    const questionnaireString = questionnaireEntry.comments.replace('Questionnaire completed: ', '');
    const questionnairePairs = questionnaireString.split(', ');

    // Parse the questionnaire data
    const questionnaireData = {};
    questionnairePairs.forEach(pair => {
      const [key, value] = pair.split(': ');
      if (key && value) {
        questionnaireData[key] = value;
      }
    });

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Questionnaire Responses</h3>
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">
              Completed on {new Date(questionnaireEntry.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Payment Terms */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Payment Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Terms of Payment</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierTermOfPayment || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Payment Method</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierPaymentMethod || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Delivery Information */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Delivery Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Delivery Terms</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierDeliveryTerms || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Mode of Delivery</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierModeOfDelivery || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Supplier Classification */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Supplier Classification</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Supplier Group</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.supplierGroup || 'Not specified'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Commodity Code</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireData.commodityCode || 'Not specified'}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-3">
            <h4 className="font-medium text-card-foreground">Additional Details</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-text-secondary">Approved By</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireEntry.approved_by || 'System'}
                </p>
              </div>
              <div>
                <label className="text-sm text-text-secondary">Approval Level</label>
                <p className="text-card-foreground font-medium">
                  {questionnaireEntry.level === 'level_1' ? 'First approval' :
                    questionnaireEntry.level === 'level_2' ? 'Level 2' :
                      questionnaireEntry.level === 'final' ? 'Final' :
                        questionnaireEntry.level?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'First approval'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Raw Data View (for debugging) */}
        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <h4 className="font-medium text-card-foreground mb-3">Raw Questionnaire Data</h4>
          <pre className="text-xs text-text-secondary whitespace-pre-wrap">
            {JSON.stringify(questionnaireData, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  const renderHistory = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">Application History</h3>
      {application.history && application.history.length > 0 ? (
        <div className="space-y-3">
          {application.history.map((event, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg">
              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  {event.status === 'approved' ? 'Approved' :
                    event.status === 'rejected' ? 'Rejected' :
                      event.status === 'pending' ? 'Under Review' : 'Status Updated'}
                </p>
                <p className="text-xs text-text-secondary">
                  {new Date(event.created_at).toLocaleString()}
                </p>
                {event.comments && (
                  <p className="text-sm text-text-secondary mt-1">{event.comments}</p>
                )}
                <p className="text-xs text-text-secondary mt-1">
                  Level: {event.level === 'level_1' ? 'First approval' :
                    event.level === 'level_2' ? 'Level 2' :
                      event.level === 'final' ? 'Final' :
                        event.level.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Clock" size={48} className="text-text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No approval history available</p>
        </div>
      )}
    </div>
  );



  const renderTabContent = () => {
    switch (activeTab) {
      case 'supplier': return renderSupplierInfo();
      case 'bank': return renderBankDetails();
      case 'documents': return renderDocuments();
      case 'agreements': return renderAgreements();
      case 'questionnaire': return renderQuestionnaire();
      case 'history': return renderHistory();
      default: return renderSupplierInfo();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-7xl w-full max-h-[calc(95vh/var(--app-scale))] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{application.company_name}</h2>
            <p className="text-sm text-slate-600 mt-1">Application Review • {application?.status?.replace('_', ' ') || 'Review'}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 bg-white flex-shrink-0">
          <nav className="flex px-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 border-b-2 font-semibold text-sm whitespace-nowrap transition-colors ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
              >
                <Icon name={tab.icon} size={18} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {renderTabContent()}
        </div>

        {/* Actions */}
        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-200 flex-shrink-0">
          {(() => {
            const isRejected = application?.status === 'REJECTED' || application?.status === 'rejected';

            // Check if vendor already has FINAL approval (from history or approvals array)
            // This handles stale LEVEL_2 PENDING records where vendor was already FINAL approved
            const hasFinalApproved = 
              (application.history && Array.isArray(application.history) && 
                application.history.some(event => 
                  (event.level === 'FINAL' || event.level === 'final') && 
                  (event.status === 'APPROVED' || event.status === 'approved')
                )) ||
              (application.approvals && Array.isArray(application.approvals) && 
                application.approvals.some(approval => 
                  (approval.level === 'FINAL' || approval.level === 'final') && 
                  (approval.status === 'APPROVED' || approval.status === 'approved')
                ));

            // Universal check: If vendor has acknowledged (APPROVED/RESOLVED), Level 1 is complete
            // This works for both LEVEL_1 and LEVEL_2 approvals
            const hasVendorAcknowledged = 
              application.acknowledgement?.status === 'APPROVED' || 
              application.acknowledgement?.status === 'RESOLVED';

            // Check if waiting for vendor acknowledgement (works for any approval level)
            const isAwaitingAcknowledgement =
              application.acknowledgement?.status === 'PENDING';

            // Vendor submitted questionnaire - awaiting admin approval
            const isQuestionnaireSubmitted =
              application.acknowledgement?.status === 'SUBMITTED';

            // Debug logging
            console.log('ReviewPanel Debug:', {
              approval_status: application?.approval_status,
              approval_level: application?.approval_level,
              acknowledgement_status: application.acknowledgement?.status,
              hasFinalApproved,
              hasVendorAcknowledged,
              isAwaitingAcknowledgement,
              isRejected,
              status: application?.status,
              historyLength: application.history?.length,
              approvalsLength: application.approvals?.length
            });

            if (isRejected) {
              return (
                <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
                  <Icon name="XCircle" size={16} />
                  <span>This vendor has been rejected and cannot be approved or rejected again.</span>
                </div>
              );
            }

            if (hasFinalApproved) {
              // Find the most recent FINAL approval date
              const finalApproval = 
                (application.history && Array.isArray(application.history) && 
                  application.history.find(event => 
                    (event.level === 'FINAL' || event.level === 'final') && 
                    (event.status === 'APPROVED' || event.status === 'approved')
                  )) ||
                (application.approvals && Array.isArray(application.approvals) && 
                  application.approvals.find(approval => 
                    (approval.level === 'FINAL' || approval.level === 'final') && 
                    (approval.status === 'APPROVED' || approval.status === 'approved')
                  ));
              
              const finalApprovalDate = finalApproval?.created_at 
                ? new Date(finalApproval.created_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'previously';

              return (
                <div className="px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 flex items-center gap-2">
                  <Icon name="CheckCircle" size={16} />
                  <span>This vendor is already FINAL approved on {finalApprovalDate}. No further actions available.</span>
                </div>
              );
            }

            const hasConflict = application.acknowledgement?.status === 'CONFLICTED';

if (hasConflict) {
  return (
    <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 flex items-center gap-2">
      <Icon name="AlertTriangle" size={16} />
      <span>
        This vendor has proposed changes to the questionnaire. 
        Please resolve the conflict in the <Link to="/conflicting-vendors" className="underline font-medium">Conflicting Vendors</Link> page before proceeding.
      </span>
    </div>
  );
}

            if (isAwaitingAcknowledgement) {
              const needsQuestionnaire = !application.supplier_term_of_payment && !application.supplier_payment_method;
              const questionnaireAlreadySubmitted =
                application.acknowledgement?.questionnaire_data != null &&
                typeof application.acknowledgement.questionnaire_data === 'object' &&
                Object.keys(application.acknowledgement.questionnaire_data).length > 0;
              if (needsQuestionnaire && isAdmin && !questionnaireAlreadySubmitted) {
                return (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600">Vendor approved. Complete the questionnaire below to send to vendor.</p>
                    <Button
                      variant="success"
                      onClick={() => setShowQuestionnaire(true)}
                      iconName="FileEdit"
                      iconPosition="left"
                    >
                      Fill Questionnaire
                    </Button>
                  </div>
                );
              }
              return (
                <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                  <Icon name="Clock" size={16} />
                  <span>Waiting for vendor acknowledgement. Approval actions will be available after vendor responds.</span>
                </div>
              );
            }

            // Only show "in Level 2 workflow" message when viewing from Level 1 context (approval_level is LEVEL_1).
            if (hasVendorAcknowledged && application?.approval_level === 'LEVEL_1') {
              return (
                <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
                  <Icon name="Info" size={16} />
                  <span>Vendor has acknowledged. First-stage approval is complete. This vendor is now in the Level 2 approval workflow.</span>
                </div>
              );
            }

            // Level 2 Pending: Assign Vendor Code button only (no Approve/Reject)
            if (application?.approval_level === 'LEVEL_2' && isAdmin && onAssignVendorCode) {
              return (
                <Button
                  variant="success"
                  onClick={() => {
                    setAssignCodeValue('');
                    setShowAssignCodeModal(true);
                  }}
                  disabled={isProcessing}
                  iconName="Hash"
                  iconPosition="left"
                >
                  Assign Vendor Code
                </Button>
              );
            }

            // Document Verification: Both Admin and Coordinator can approve/reject
            // Level 1: Only Admin can approve/reject
            const isDocumentVerification = isDocumentVerificationMode;
            const isLevel1 = application?.approval_level === 'LEVEL_1';
            const canApprove = isDocumentVerification || (isLevel1 && isAdmin);
            const hasRejectedDocs = application?.documents?.some(doc => doc.status === 'REJECTED');
            const approveDisabled = isProcessing || (isDocumentVerification && hasRejectedDocs);

            if (canApprove) {
              return (
                <>
                  {isDocumentVerification && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedDocuments(new Set());
                        setMultiRejectReason('');
                        setShowMultiRejectModal(true);
                      }}
                      disabled={isProcessing}
                      iconName="FileText"
                      iconPosition="left"
                    >
                      Request Document Replacement
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    onClick={() => handleAction('reject')}
                    disabled={isProcessing}
                    iconName="XCircle"
                    iconPosition="left"
                  >
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => handleAction('approve')}
                    disabled={approveDisabled}
                    loading={isProcessing}
                    iconName="CheckCircle"
                    iconPosition="left"
                  >
                    {isDocumentVerification ? 'Documents Verification Confirmation' : 'Approve'}
                  </Button>
                </>
              );
            }

            // Coordinator viewing Level 1 or Level 2
            if ((isLevel1 || application?.approval_level === 'LEVEL_2') && !isAdmin) {
              return (
                <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                  <Icon name="Lock" size={16} />
                  <span>Only admins can approve vendors in the Documents Verified and Level 2 stages. You can view vendor details.</span>
                </div>
              );
            }

            // Fallback for other cases
            return (
              <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
                <Icon name="Lock" size={16} />
                <span>You have view-only access. Only admins can approve or reject applications.</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Rejection Type Selection Modal */}
      {showRejectionOptions && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Select Rejection Action</h3>
              <p className="text-sm text-slate-600 mt-1">Choose how you want to reject this vendor</p>
            </div>
            
            <div className="p-6 space-y-4">
              <Button
                variant="destructive"
                onClick={() => handleSelectRejectionType('permanent')}
                className="w-full"
                iconName="XCircle"
                iconPosition="left"
              >
                Reject Vendor (Permanent)
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSelectRejectionType('rectification')}
                className="w-full"
                iconName="Edit"
                iconPosition="left"
              >
                Reject with Correction
              </Button>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancelRejection}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Rejection Confirmation Dialog */}
      {showPermanentRejectionConfirmation && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[102] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border-2 border-red-500 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-red-200 bg-red-50">
              <h3 className="text-xl font-bold text-red-900">⚠️ Warning</h3>
            </div>
            
            <div className="p-6">
              <p className="text-lg font-semibold text-red-900 text-center mb-4">
                YOU HAVE CHOOSED TO REJECT THE VENDOR. ALL DETAILS OF THIS VENDOR WILL BE DELETED PERMANENTLY. WISH TO PROCEED?
              </p>
            </div>
            
            <div className="p-6 border-t border-red-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancelRejection}
              >
                NO / CANCEL
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmPermanentRejection}
              >
                YES
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Form Modal */}
      {showRejectionForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">
                {rejectionType === 'rectification' ? 'Reject with Correction' : 'Reject Application'}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {rejectionType === 'rectification' 
                  ? 'Please provide remarks about what needs to be corrected'
                  : 'Please provide a reason for rejection'}
              </p>
            </div>

            <div className="p-6 space-y-4">
              {rejectionType === 'permanent' && (
                <>
                  <Select
                    label="Rejection Reason *"
                    placeholder="Select reason for rejection"
                    options={rejectionReasons}
                    value={rejectionReason}
                    onChange={setRejectionReason}
                  />
                  
                  {rejectionReason === 'other' && (
                    <Input
                      label="Custom Reason *"
                      type="text"
                      placeholder="Specify the reason..."
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                    />
                  )}
                </>
              )}

              <Input
                label={rejectionType === 'rectification' ? 'Additional Remarks *' : 'Additional Remarks'}
                type="textarea"
                placeholder={rejectionType === 'rectification' ? 'Specify what needs to be corrected...' : 'Any additional comments...'}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>

            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancelRejection}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={isProcessing}
                loading={isProcessing}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Questionnaire Modal */}
      {showQuestionnaire && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-[98vw] max-h-[calc(98vh/var(--app-scale))] h-[calc(95vh/var(--app-scale))] flex flex-col relative">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900">
                {isIndianVendor ? 'Indian Vendor' : 'Foreign Vendor'} Questionnaire
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowQuestionnaire(false)}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              {isIndianVendor ? (
                <IndianVendorQuestionnaire
                  formData={questionnaireData}
                  updateFormData={updateQuestionnaireData}
                  errors={questionnaireErrors}
                />
              ) : (
                <ForeignVendorQuestionnaire
                  formData={questionnaireData}
                  updateFormData={updateQuestionnaireData}
                  errors={questionnaireErrors}
                />
              )}
            </div>

            <div className="p-6 border-t border-border flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowQuestionnaire(false)}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={handleQuestionnaireSubmit}
                disabled={isProcessing}
                loading={isProcessing}
              >
                Send to Vendor
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Vendor Code Modal */}
      {showAssignCodeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[102] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Assign Vendor Code</h3>
              <p className="text-sm text-slate-600 mt-1">
                Enter a unique vendor code for {application?.company_name || 'this vendor'}, or leave blank to auto-generate.
              </p>
            </div>
            <div className="p-6">
              <input
                type="text"
                value={assignCodeValue}
                onChange={(e) => setAssignCodeValue(e.target.value)}
                placeholder="e.g. VND0001"
                className="w-full border border-slate-300 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
              />
            </div>
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAssignCodeModal(false);
                  setAssignCodeValue('');
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={handleAssignVendorCodeSubmit}
                disabled={isProcessing}
                loading={isProcessing}
              >
                Assign
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Request Document Replacement Modal */}
      {showMultiRejectModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[103] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-lg relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Request Document Replacement</h3>
              <p className="text-sm text-slate-600 mt-1">
                Select the documents that need to be replaced. Vendor will receive an email with a link to upload replacements.
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select documents to replace <span className="text-red-500">*</span>
                </label>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                  {documentsEligibleForReplacementRequest.length === 0 ? (
                    <p className="text-sm text-slate-500">No pending documents to replace.</p>
                  ) : (
                    documentsEligibleForReplacementRequest.map((doc) => (
                      <label key={doc.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 rounded p-2 -m-2">
                        <input
                          type="checkbox"
                          checked={selectedDocuments.has(doc.id.toString())}
                          onChange={() => toggleDocumentSelection(doc.id.toString())}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-slate-700">
                          {formatDocumentType(doc.document_type)} - {doc.file_name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={multiRejectReason}
                  onChange={(e) => setMultiRejectReason(e.target.value)}
                  placeholder="Explain why these documents need to be replaced and what should be corrected..."
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowMultiRejectModal(false);
                  setMultiRejectReason('');
                  setSelectedDocuments(new Set());
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectSelectedDocuments}
                disabled={isProcessing || !multiRejectReason.trim() || selectedDocuments.size === 0}
                loading={isProcessing}
              >
                <Icon name="Send" size={14} className="mr-1" />
                Request Replacement
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPanel;